import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Toast from '@plone/volto/components/manage/Toast/Toast';

import { getMpfNotificacoesContagem } from 'volto-ministerio-publico-federal/actions';
import { podeVerToolbar } from 'volto-ministerio-publico-federal/helpers/Permissoes';
import { proximoIntervalo } from 'volto-ministerio-publico-federal/helpers/Notificacoes';

/**
 * Mantém o contador do sino atualizado. Registrado em `appExtras`: roda em
 * todas as rotas e não renderiza nada.
 *
 * - só para quem está autenticado e tem o sino liberado pelo perfil;
 * - a cada minuto, só com a aba visível (volta a buscar ao voltar para ela);
 * - backoff exponencial em erro, até 5 minutos;
 * - tudo em efeito: no SSR o contador nasce zerado e não há badge para
 *   divergir na hidratação.
 *
 * Quando o contador sobe, avisa com UM toast discreto (nunca um por polling).
 */
const NotificacoesPoller = () => {
  const dispatch = useDispatch();
  const token = useSelector((state: any) => state.userSession?.token);
  const permissoes = useSelector((state: any) => state.mpfPermissoes?.data);
  const naoLidas: number = useSelector(
    (state: any) => state.mpfNotificacoes?.contagem?.nao_lidas ?? 0,
  );
  const carregada: boolean = useSelector(
    (state: any) => state.mpfNotificacoes?.contagemCarregada ?? false,
  );
  const habilitado = !!token && podeVerToolbar(permissoes, 'notifications');

  const falhas = useRef(0);
  const anterior = useRef<number | null>(null);

  useEffect(() => {
    if (!habilitado) return undefined;
    let ativo = true;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const limpar = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };

    const buscar = async () => {
      limpar();
      if (!ativo || document.visibilityState === 'hidden') return;
      try {
        await (dispatch as any)(getMpfNotificacoesContagem());
        falhas.current = 0;
      } catch {
        falhas.current += 1;
      }
      if (ativo) timer = setTimeout(buscar, proximoIntervalo(falhas.current));
    };

    const aoMudarVisibilidade = () => {
      if (document.visibilityState === 'visible') buscar();
    };

    buscar();
    document.addEventListener('visibilitychange', aoMudarVisibilidade);
    return () => {
      ativo = false;
      limpar();
      document.removeEventListener('visibilitychange', aoMudarVisibilidade);
    };
  }, [dispatch, habilitado]);

  useEffect(() => {
    if (!carregada) {
      anterior.current = null;
      return;
    }
    if (anterior.current !== null && naoLidas > anterior.current) {
      const novas = naoLidas - anterior.current;
      toast.info(
        <Toast
          info
          title="Notificações"
          content={
            novas === 1
              ? 'Você tem uma nova notificação.'
              : `Você tem ${novas} novas notificações.`
          }
        />,
        { toastId: 'mpf-notificacoes-novas' },
      );
    }
    anterior.current = naoLidas;
  }, [naoLidas, carregada]);

  return null;
};

export default NotificacoesPoller;
